import {
  Text,
  Panel,
  Input,
  Unless,
  Select,
  Button,
  FormField,
} from '../../../..'
import {
  SaveMortgage,
  DeleteMortgage,
} from '../../../../../constants/trackingEvents'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import { useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { LOAN_STATUS, LOAN_TYPE } from '../../../../../constants/loan'
import withComposedClassName from '../../../../../composables/withComposedClassName'

import styles from './EditLoan.module.scss'

const StyledSelect = withComposedClassName(styles.select)(Select)

const LoanStatus = ({ status, onChangeStatus }) => (
  <div>
    <Text variant='neutralCool' variantCount='700' element='span' size='xs'>
      <FormattedMessage
        id='Mortgages.edit.status'
        defaultMessage='This loan is'
      />
    </Text>

    <div role='radiogroup' className={styles.radiogroup}>
      <label className={styles.radio}>
        <Input
          className={styles.input}
          type='radio'
          name='status'
          checked={status === LOAN_STATUS.CURRENT}
          onChange={onChangeStatus(LOAN_STATUS.CURRENT)}
        />
        <Text size='xs' element='span' variant='neutralCool' variantCount='700'>
          <FormattedMessage
            id='Mortgages.edit.loanStatus.active'
            defaultMessage='Active'
          />
        </Text>
      </label>
      <label className={styles.radio}>
        <Input
          className={styles.input}
          type='radio'
          name='status'
          checked={status === LOAN_STATUS.EXPIRED}
          onChange={onChangeStatus(LOAN_STATUS.EXPIRED)}
        />
        <Text size='xs' element='span' variant='neutralCool' variantCount='700'>
          <FormattedMessage
            id='Mortgages.edit.loanStatus.expired'
            defaultMessage='Expired'
          />
        </Text>
      </label>
    </div>
  </div>
)

const EditLoan = ({
  adding,
  amount,
  deleting,
  saving,
  cancelEdit,
  deleteLoan,
  interestRate,
  monthOptions,
  onChangeAmount,
  onChangeInterestRate,
  onChangePmiMonthly,
  onChangeMonth,
  onChangeTerm,
  onChangeType,
  onChangeYear,
  onChangeStatus,
  pmiMonthly,
  selectedMonth,
  selectedTerm,
  selectedType,
  selectedYear,
  status,
  termOptions,
  typeOptions,
  onSubmit,
  yearOptions,
}) => {
  const { ampli } = useContext(AmplitudeContext)

  const handleSubmit = e => {
    ampli?.clientCustomized({
      customization_type: AmplitudeEventConstants.CLIENT_UPDATED_LOAN,
    })

    onSubmit(e)
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <Panel.Heading>
        <FormattedMessage
          id='Mortgages.heading.edit'
          defaultMessage='Edit your mortgage'
        />
      </Panel.Heading>

      <div className={styles.stackable}>
        <FormField.Dollars
          required
          value={amount}
          onChange={onChangeAmount}
          data-qa='mortgage-loan-amount-input'
        >
          <FormattedMessage
            id='Mortgages.edit.amount'
            defaultMessage='How much was the loan for?'
          />
        </FormField.Dollars>

        <div role='group' aria-labelledby='field-set-legend'>
          <Text
            className={styles.label}
            size='xs'
            id='field-set-legend'
            variant='neutralCool'
            variantCount='700'
          >
            <FormattedMessage
              id='Mortgages.edit.startDate'
              defaultMessage='Date the loan started'
            />
          </Text>
          <div className={styles.layoutFlex}>
            <FormField
              name='month'
              data-qa='mortgage-month-input'
              hideLabel
              input={StyledSelect}
              value={selectedMonth}
              options={monthOptions}
              onChange={onChangeMonth}
            >
              <FormattedMessage
                id='Mortgages.edit.startMonth'
                defaultMessage='Month the loan started'
              />
            </FormField>
            <FormField
              name='year'
              data-qa='mortgage-year-input'
              hideLabel
              input={StyledSelect}
              value={selectedYear}
              options={yearOptions}
              onChange={onChangeYear}
            >
              <FormattedMessage
                id='Mortgages.edit.startYear'
                defaultMessage='Year the loan started'
              />
            </FormField>
          </div>
        </div>
      </div>

      <div className={styles.stackable}>
        <FormField.Rate
          className={styles.input}
          data-qa='mortgage-interest-input'
          required
          value={interestRate}
          onChange={onChangeInterestRate}
        >
          <FormattedMessage
            id='Mortgages.edit.interestRate'
            defaultMessage='Interest rate'
          />
        </FormField.Rate>

        <FormField
          data-qa='mortgage-type-input'
          input={StyledSelect}
          name='loanType'
          value={selectedType}
          optionKey='value'
          options={typeOptions}
          onChange={onChangeType}
        >
          <FormattedMessage
            id='Mortgages.edit.type'
            defaultMessage='What type of loan is this?'
          />
        </FormField>
      </div>

      <div className={styles.stackable}>
        <Unless condition={selectedType.loanType === LOAN_TYPE.HELOC}>
          <FormField
            input={StyledSelect}
            name='loanTerm'
            value={selectedTerm}
            optionKey='value'
            options={termOptions}
            onChange={onChangeTerm}
          >
            <FormattedMessage
              id='Mortgages.edit.term'
              defaultMessage='The loan term'
            />
          </FormField>
        </Unless>

        <FormField.DollarsAndCents
          value={pmiMonthly}
          onChange={onChangePmiMonthly}
        >
          <FormattedMessage
            id='Mortgages.edit.pmi'
            defaultMessage='Monthly mortgage insurance'
          />
        </FormField.DollarsAndCents>
      </div>

      <Unless condition={adding}>
        <LoanStatus status={status} onChangeStatus={onChangeStatus} />
      </Unless>

      <div className={styles.buttonGroup}>
        <div className={styles.buttons}>
          <Button
            data-qa='mortgage-submit-button'
            primary
            disabled={saving || deleting}
            loading={saving}
            type='submit'
            fullWidth
            trackEnabled={!adding}
            track={SaveMortgage}
          >
            <FormattedMessage
              id='Mortgages.edit.saveButton'
              defaultMessage='Save changes'
            />
          </Button>
          <Button
            minimal
            type='button'
            fullWidth
            disabled={saving || deleting}
            onClick={cancelEdit}
          >
            <FormattedMessage
              id='Mortgages.edit.cancelButton'
              defaultMessage='Cancel'
            />
          </Button>
        </div>
        <Unless condition={adding}>
          <hr className={styles.divider} />
          <Button
            danger
            minimal
            data-qa='delete-loan-button'
            onClick={deleteLoan}
            disabled={saving || deleting}
            loading={deleting}
            track={DeleteMortgage}
            className={styles.deleteLoan}
          >
            <FormattedMessage
              id='Mortgages.edit.deleteButton'
              defaultMessage='Delete this loan'
            />
          </Button>
        </Unless>
      </div>
    </form>
  )
}

export default EditLoan
